import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Skeleton } from 'antd';
import { CheckSquareOutlined, ReadOutlined, RightOutlined, SafetyCertificateOutlined, WarningOutlined } from '@ant-design/icons';

import { standardsApi } from '../lib/wikiApi';
import { learnApi, type LearnCourseListItem } from '../lib/api/learn';
import { PageTitle, StatCard, Tag } from '../components/ds';
import { OpeningTasksSection, useOpeningTasks } from '../components/opening-tasks/OpeningTasksSection';
import './Learn.css';
import './MyTasksOpening.css';

/**
 * Мои задачи (макет кита TasksProfilePage) — агрегатор личных дел из
 * РЕАЛЬНЫХ данных. НЕ генерик-модель Task: собираем из существующих
 * сущностей через educationApi + standardsApi.
 *
 *   • стандарты на подтверждение (requiresAck, !isAcked) — отдельно
 *     просроченные (effectiveFrom прошёл) и в срок;
 *   • назначенные курсы с дедлайном (isAssigned, !isCompleted);
 *   • незавершённые/обязательные курсы.
 *
 * «Done» = действие: кнопки ведут на нужную страницу (ack статьи /
 * прохождение модуля), отдельного POST done нет. HUB 2.0: «Продолжить»
 * ведёт сразу в урок, с которого продолжать (continueLessonId).
 *
 * Партнёр на открытии (владелец 06.10.2026): сверху блок «Открытие» —
 * components/opening-tasks. С карточкой открытия (владелец 31.10.2026) тут
 * только текущий этап: задачи «в работе» и заявка на переход; весь план
 * (Гант, этапы, чек-листы, помещения, обращения) — в визарде /opening.
 * Без карточки, но с помещениями — прежний экран: плитки, помещения с
 * перепиской, траектория и уведомления. Стандарты и курсы остаются ниже.
 * Не партнёр / нет открытия — страница как раньше.
 */
export function MyTasksPage(): JSX.Element {
  const opening = useOpeningTasks();
  const [sp] = useSearchParams();
  const coursesQuery = useQuery({ queryKey: ['wiki', 'education', 'courses'], queryFn: () => learnApi.listCourses(), retry: false });
  const standardsQuery = useQuery({ queryKey: ['wiki', 'standards'], queryFn: () => standardsApi.get(), retry: false });

  const courses = coursesQuery.data ?? [];
  const now = Date.now();

  // Курсы: назначенные с дедлайном (приоритет) → продолжить → обязательные не начаты.
  const assigned = courses.filter((c) => c.isAssigned && !c.isCompleted);
  const assignedIds = new Set(assigned.map((c) => c.id));
  const inProgress = courses.filter((c) => !assignedIds.has(c.id) && c.progressPercent > 0 && !c.isCompleted);
  const notStarted = courses.filter((c) => !assignedIds.has(c.id) && c.progressPercent === 0 && c.isRequired);

  // Стандарты: непрочитанные, разбитые на просроченные / в срок.
  const pendingStandards = (standardsQuery.data?.items ?? []).filter((s) => !s.isAcked);
  const overdueStandards = pendingStandards.filter((s) => s.effectiveFrom && new Date(s.effectiveFrom).getTime() < now);
  const dueStandards = pendingStandards.filter((s) => !(s.effectiveFrom && new Date(s.effectiveFrom).getTime() < now));

  // Ждём и ответ по открытию, чтобы страница не перестраивалась рывком под партнёра.
  const loading = coursesQuery.isLoading || standardsQuery.isLoading || opening.pending;
  const totalTasks = pendingStandards.length + assigned.length + inProgress.length + notStarted.length;
  const isOpening = opening.active;
  // Карточка открытия есть — помещения и переписка переехали в визард (в «Обращения»).
  const hasCard = isOpening && !!opening.query.data?.pipeline;

  // Старые ссылки (уведомления, «Открыть переписку →») ведут на /tasks?room=<id>: с карточкой открытия — в визард.
  const roomParam = sp.get('room');
  if (hasCard && roomParam) {
    return <Navigate replace to={`/opening?tab=appeals&room=${encodeURIComponent(roomParam)}`} />;
  }

  return (
    <div style={{ maxWidth: hasCard ? 960 : isOpening ? 1280 : 860 }}>
      <PageTitle
        title="Мои задачи"
        sub={
          hasCard
            ? 'Открытие точки: задачи текущего этапа и заявка на переход на следующий'
            : isOpening
              ? 'Открытие точки: задачи этапа, ваши помещения и переписка с куратором'
              : 'Что нужно прочитать, пройти и подтвердить'
        }
      />

      <OpeningTasksSection state={opening} />

      {isOpening ? (
        !loading && totalTasks > 0 ? (
          <h2 className="fk-ot-older">Стандарты и обучение</h2>
        ) : null
      ) : !loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 4 }}>
          <StatCard icon={<CheckSquareOutlined />} tone="orange" value={totalTasks} label="активные задачи" />
          <StatCard icon={<WarningOutlined />} tone="red" value={overdueStandards.length} label="просрочено" />
          <StatCard icon={<SafetyCertificateOutlined />} tone="green" value={pendingStandards.length} label="стандартов на подтверждение" to="/standards" />
        </div>
      )}

      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 20 }} />
      ) : totalTasks === 0 ? (
        // На открытии задачи — в блоке выше; «Всё сделано» тут противоречило бы ему.
        isOpening ? null : (
          <div className="fk-learn-card" style={{ marginTop: 20, textAlign: 'center', padding: 48 }}>
            <CheckSquareOutlined style={{ fontSize: 40, color: 'var(--fk-success)' }} />
            <div style={{ fontWeight: 700, fontSize: 16, marginTop: 12 }}>Всё сделано 🎉</div>
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Нет активных задач — отличная работа!</div>
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: isOpening ? 12 : 20, maxWidth: 860 }}>
          {overdueStandards.length > 0 && (
            <Section title="Просроченные стандарты" count={overdueStandards.length}>
              {overdueStandards.slice(0, 20).map((s) => (
                <TaskRow key={s.id} to={`/article/${s.slug}`} icon={<WarningOutlined />} color="var(--fk-error)"
                  title={s.title} sub={`${s.category} · действует с ${fmtDate(s.effectiveFrom)}`} action="Подтвердить" />
              ))}
            </Section>
          )}
          {dueStandards.length > 0 && (
            <Section title="Стандарты на подтверждение" count={dueStandards.length}>
              {dueStandards.slice(0, 20).map((s) => (
                <TaskRow key={s.id} to={`/article/${s.slug}`} icon={<SafetyCertificateOutlined />} color="var(--fk-orange)"
                  title={s.title} sub={`${s.versionLabel} · ${s.category}`} action="Подтвердить" />
              ))}
            </Section>
          )}
          {assigned.length > 0 && (
            <Section title="Назначенные курсы" count={assigned.length}>
              {assigned.map((c) => (
                <CourseRow key={c.id} c={c} action={c.progressPercent > 0 ? 'Продолжить' : 'Начать'} />
              ))}
            </Section>
          )}
          {inProgress.length > 0 && (
            <Section title="Продолжить обучение" count={inProgress.length}>
              {inProgress.map((c) => (
                <CourseRow key={c.id} c={c} action="Продолжить" />
              ))}
            </Section>
          )}
          {notStarted.length > 0 && (
            <Section title="Обязательные курсы (не начаты)" count={notStarted.length}>
              {notStarted.map((c) => (
                <TaskRow key={c.id} to={`/course/${c.id}`} icon={<ReadOutlined />} color="var(--fk-error)"
                  title={c.name} sub="Обязательное обучение" action="Начать" />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function CourseRow({ c, action }: { c: LearnCourseListItem; action: string }): JSX.Element {
  const lessons = c.totalLessons ?? c.totalModules;
  const done = c.completedLessons ?? c.completedModules;
  const sub = [
    `${done}/${lessons} уроков`,
    c.dueAt ? `срок: ${fmtDate(c.dueAt)}` : null,
    c.continueLessonTitle && c.progressPercent > 0 ? `дальше: «${c.continueLessonTitle}»` : null,
  ].filter(Boolean).join(' · ');
  // «Продолжить» — сразу в урок; «Начать» — на страницу курса (обзор программы).
  const to = c.progressPercent > 0 && c.continueLessonId ? `/lesson/${c.continueLessonId}` : `/course/${c.id}`;
  return (
    <TaskRow to={to} icon={<ReadOutlined />} color="var(--fk-info)"
      title={c.name} sub={sub} progress={c.progressPercent > 0 ? c.progressPercent : undefined} action={action} />
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h2 className="fk-learn-h2" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        {title} <Tag>{count}</Tag>
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </section>
  );
}

function TaskRow({ to, icon, color, title, sub, progress, action }: {
  to: string; icon: React.ReactNode; color: string; title: string; sub: string; progress?: number; action: string;
}): JSX.Element {
  return (
    <Link to={to} className="fk-learn-card" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', minHeight: 64 }}>
      <div style={{ width: 40, height: 40, borderRadius: 6, background: `color-mix(in srgb, ${color} 12%, transparent)`, color, display: 'grid', placeItems: 'center', fontSize: 18, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
        {typeof progress === 'number' && (
          <div className="fk-learn-bar" style={{ marginTop: 6, maxWidth: 240, height: 4 }}><span style={{ width: `${progress}%` }} /></div>
        )}
      </div>
      <span style={{ flexShrink: 0, color: 'var(--fk-orange)', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{action} <RightOutlined style={{ fontSize: 11 }} /></span>
    </Link>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}
